import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

// Layout/UI
import { Box, Avatar, AvatarGroup, Link, Tabs, Text, MetreaTooltip } from "@metrea-ui/v2";
import Navbar, { NavbarStart, NavbarCenter, NavbarEnd, Logo } from "../UI/Navbar/Navbar";

import styles from "./Header.module.css";

import { isAdmin, isDeveloper, isStrat, isUserLogin, handleLogin } from "src/utils/sessionUtils";
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { debugLog } from "src/utils/debugUtils";
import { mColor } from "src/utils/themeUtils";

import MainMenu, { ProfileMenu } from "./MainMenu";

const Header = (props) => {
	const [landingPage, setLandingPage] = useState(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		setLandingPage(window.location.pathname === "/" && !window.location.href.includes("?getCode=true"));
	}, []);

	const logoClickHandler = () => {
		window.location.replace(process.env.REACT_APP_URI);
	};

	let accessAction = (
		<MetreaTooltip content="Sign In">
			<Box
				flex="0 0 auto"
				p={0}
				cursor="pointer"
				display="flex"
				alignItems="center"
				justifyContent="flex-end"
				color={mColor.MidnightBlue}
				gap="12px"
				onClick={handleLogin}
			>
				<Box
					className="hide_480"
					style={{ flex: "0 0 auto", textAlign: "right", alignItems: "center" }}
				>
					Sign In
				</Box>
				<AvatarGroup>
					<Avatar.Root size="2xs">
						<Avatar.Fallback />
						<Avatar.Image />
					</Avatar.Root>
				</AvatarGroup>
			</Box>
		</MetreaTooltip>
	);

	// Subscribe to Redux session state
	const { currUser, loginUser } = useSelector((state) => state.session);
	const cu = currUser || {};
	const lu = loginUser || {};

	if (isUserLogin()) {
		const isActing = cu?.id && lu?.id && cu.id !== lu.id;
		const displayName = cu?.full_name?.trim() !== "" ? cu.full_name : cu?.email || "Guest";
		const roleLabel =
			Array.isArray(cu?.user_roles) && cu.user_roles.length > 0
				? getValuesByPropertyPath(cu.user_roles, "name").join(", ")
				: "Basic User";
		const jobTitleLabel = cu?.job_title || "No Job Title";

		const userInfo = {
			...cu,
			isActing: isActing,
			displayName: (isActing ? " Acting as " : "") + displayName,
			jobTitleLabel: jobTitleLabel,
			roleLabel: roleLabel,
		};

		accessAction = (
			<Box
				style={{
					flex: "0 0 auto",
					padding: 0,
					alignItems: "center",
					display: "flex",
					justifyContent: "end",
					justifyItems: "end",
					color: mColor.MidnightBlue,
					gap: "12px",
				}}
			>
				<Box
					className="hide_480"
					style={{
						flex: "0 0 auto",
						textAlign: "right",
						alignItems: "center",
						whiteSpace: "nowrap",
					}}
				>
					{displayName}
				</Box>
				<ProfileMenu user={userInfo} landingPage={landingPage} />
			</Box>
		);
	}

	return (
		<>
			<header>
				{/* <div data-theme="light" className="headerNavbar"> */}
				<Navbar variant="slim" transparent="true" showRibbon="true" wrapperClasses="">
					<NavbarStart className="flex-none flex items-center gap-2">
						<Logo appName="Raven" onClick={logoClickHandler} />
					</NavbarStart>
					<NavbarCenter className="flex-auto flex visible">
						{isUserLogin() && (
							// Use Chakra breakpoints (base/sm/md/...) and explicit display values.
							// Show module tabs from md up; hide on small screens.
							<Box flex="1 1 100%" display={{ base: "none", md: "flex" }} pl="3rem">
								{/* <ModuleNavigationMenu /> */}
								<ModuleNavigationTab />
							</Box>
						)}
						{isUserLogin() && (
							//  Opposite visibility: show on small screens, hide from md up.
							<Box flex="1 1 100%" display={{ base: "flex", md: "none" }}>
								<MainMenu session={props.session} />
							</Box>
						)}
					</NavbarCenter>
					<NavbarEnd className="flex items-center">
						<div className="flex-initial justify-items-end flex">{accessAction}</div>
					</NavbarEnd>
				</Navbar>
				{/* </div> */}
			</header>
		</>
	);
};

export default Header;

export const PageHeader = (props) => {
	return (
		<Box display="flex" flexDirection="row" alignItems="center">
			<Text
				textStyle="lg"
				style={{
					flex: "1 1 auto", // let title grow naturally
					padding: "18px 0",
					fontSize: "24px",
					fontWeight: 400,
					lineHeight: "26px",
				}}
			>
				{props.pageTitle}
			</Text>
			{props.actions && (
				<Box
					ml="auto"
					display="flex"
					justifyContent="flex-end"
					alignItems="center"
					gap="10px"
					flexWrap="wrap"
				>
					{props.children &&
						Array.isArray(props.children) &&
						props.children.map((child, index) => {
							return (
								<Box flex="0 0 auto" py="15px" key={index}>
									{child}
								</Box>
							);
						})}
					{props.children && !Array.isArray(props.children) && (
						<Box flex="0 0 auto" py="15px">
							{props.children}
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};

export const ModuleNavigationMenu = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const menuClickHandler = (route) => {
		navigate(route);
		debugLog(3, "[Header][ACTION] moduleNavigationMenuClick", { route, module: "Header" });
	};

	const MENUS = [
		{ label: "Baselines", path: "/baseline", show: isUserLogin() },
		{ label: "Scenarios", path: "/scenario", show: isUserLogin() },
		{ label: "Auctions", path: "/auction", show: isUserLogin() && isStrat() },
		{ label: "Admin", path: "/admin", show: isUserLogin() && (isAdmin() || isDeveloper()) },
	];

	return (
		<>
			{isUserLogin() && (
				<Box flex="1 1 50%" display="flex" justifyContent="center" alignItems="center" gap={0}>
					{MENUS.filter((t) => t.show).map((t) => {
						const active = location.pathname === t.path;
						return (
							<Box key={t.path}>
								<span
									className={active ? styles.menuSelected : styles.menu}
									onClick={() => menuClickHandler(t.path)}
									role="tab"
									aria-selected={active}
								>
									{t.label}
								</span>
							</Box>
						);
					})}
				</Box>
			)}
		</>
	);
};

export const ModuleNavigationTab = () => {
	const navigate = useNavigate();
	const location = useLocation();

	// derive current module from the URL, with a safe fallback
	const pathSeg = location.pathname.split("/")[1] || "";
	const module = ["baseline", "scenario", "auction", "admin"].includes(pathSeg) ? pathSeg : "scenario";

	const MENUS = [
		{ label: "Baselines", value: "baseline", path: "/baseline", show: isUserLogin() },
		{ label: "Scenarios", value: "scenario", path: "/scenario", show: isUserLogin() },
		{ label: "Auctions", value: "auction", path: "/auction", show: isUserLogin() && isStrat() },
		{
			label: "Admin",
			value: "admin",
			path: "/admin",
			show: isUserLogin() && (isAdmin() || isDeveloper()),
		},
	];

	const handleTabClick = (e, route) => {
		debugLog(3, "[Header][ACTION] moduleNavigationTabClick", {
			location: location,
			module: module,
			route: route,
		});
		navigate(route);
	};

	return (
		// Make Tabs controlled so selection tracks the URL
		<Tabs.Root
			value={module}
			// key ensures proper remount if your Tabs lib caches internal state
			key={module}
			variant="plain"
			className={styles["mod-tabs"]}
		>
			<Tabs.List className={styles["mod-tabs-list"]}>
				{MENUS.filter((t) => t.show).map((t) => {
					return (
						<Tabs.Trigger key={t.value} value={t.value} asChild className={styles["mod-tab"]}>
							<Link
								unstyled
								onClick={(e) => handleTabClick(e, `/${t.value}`)}
								className={styles["mod-tab"]}
							>
								{t.label}
							</Link>
						</Tabs.Trigger>
					);
				})}
				<Tabs.Indicator className={styles["mod-tabs-indicator"]} />
			</Tabs.List>
		</Tabs.Root>
	);
};
